/**
 * Extracts unique color codes from product images and metadata.
 * @param {Object} product - The product object.
 * @returns {string[]} Array of unique color codes.
 */
export const getProductValue = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    return val.name || val.title || val.label || "";
  }
  return String(val);
};

/** Decode route params that may arrive percent-encoded (Persian slugs). */
export const decodeProductRouteParam = (productId) => {
  if (productId == null || productId === "") return "";
  let value = String(productId);

  // Next may pass already-decoded, once-encoded, or double-encoded params
  for (let i = 0; i < 2; i += 1) {
    if (!value.includes("%")) break;
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch {
      break;
    }
  }

  // Normalize Unicode so Persian/Arabic forms compare reliably
  return typeof value.normalize === "function" ? value.normalize("NFC") : value;
};

/** Extract numeric product id from /product/[slug]-[id] route param. */
export const parseProductIdFromParam = (productId) => {
  const value = decodeProductRouteParam(productId);
  if (!value) return null;
  const match = value.match(/-(\d+)$/);
  return match ? match[1] : value.split("-").pop() || null;
};

/** Canonical /product/[slug]-[id] segment for a product. */
export const getProductRouteParam = (product) => {
  const id = product?.id;
  if (!id) return null;
  const slug = product?.slug || "product";
  return decodeProductRouteParam(`${slug}-${id}`);
};

/** True only when the URL segment matches the product slug-id exactly. */
export const productMatchesRouteParam = (product, productIdParam) => {
  const expected = getProductRouteParam(product);
  if (!expected || productIdParam == null || productIdParam === "") return false;
  return decodeProductRouteParam(productIdParam) === expected;
};

/** Normalize single-product API payloads to a product object with id. */
export const normalizeProduct = (response) => {
  const candidate =
    response?.product ||
    response?.data?.product ||
    (response?.data?.id ? response.data : null) ||
    (response?.id ? response : null);

  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }

  if (!candidate.id && !candidate._id && !candidate.uuid) {
    return null;
  }

  return candidate;
};

export const getProductHref = (product) => {
  const param = getProductRouteParam(product);
  if (!param) return null;
  return `/product/${param}`;
};

export const getProductColors = (product) => {
  if (!product) return [];

  const colors = new Set();

  // 1. Extract from images_meta (highest priority/latest data)
  if (product.images_meta) {
    try {
      const meta = typeof product.images_meta === "string" 
        ? JSON.parse(product.images_meta) 
        : product.images_meta;
      
      if (Array.isArray(meta)) {
        meta.forEach(m => {
          const code = m.code || m.color;
          if (code) {
            const finalCode = typeof code === "object" ? (code.name || code.title || code.code) : code;
            if (finalCode) colors.add(finalCode);
          }
        });
      }
    } catch (e) {
      // Silently fail parsing
    }
  }

  // 2. Extract from images array
  if (Array.isArray(product.images)) {
    product.images.forEach(img => {
      const code = img.code || img.color;
      if (code) {
        const finalCode = typeof code === "object" ? (code.name || code.title || code.code) : code;
        if (finalCode) colors.add(finalCode);
      }
    });
  }

  return Array.from(colors);
};

/**
 * Merges images with their metadata (colors, etc.)
 * @param {Object} product - The product object.
 * @returns {Object[]} Array of image objects with url and metadata.
 */
export const getProductImagesWithMeta = (product) => {
  if (!product) return [];
  
  const images = Array.isArray(product.images) ? product.images : [];
  let meta = [];
  
  if (product.images_meta) {
    try {
      meta = typeof product.images_meta === "string" 
        ? JSON.parse(product.images_meta) 
        : product.images_meta;
    } catch (e) {
      meta = [];
    }
  }
  
  return images.map((img, index) => {
    const imgObj = typeof img === 'string' ? { url: img } : { ...img };
    
    // Normalize url/image field
    if (!imgObj.url && imgObj.image) imgObj.url = imgObj.image;
    
    // Merge with meta if available at same index
    const m = Array.isArray(meta) ? meta[index] : null;
    if (m) {
      const normalizedMeta = { ...m };
      // Normalize common fields that might be objects
      ['code', 'color', 'title', 'name', 'label'].forEach(field => {
        if (normalizedMeta[field] && typeof normalizedMeta[field] === "object") {
          normalizedMeta[field] = normalizedMeta[field].name || normalizedMeta[field].title || normalizedMeta[field].code || normalizedMeta[field].label || "";
        }
      });
      return { ...imgObj, ...normalizedMeta };
    }
    return imgObj;
  });
};

